import { format, subMonths } from "date-fns";
import { enGB, es } from "date-fns/locale";
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import Select, { ActionMeta, ValueType } from "react-select";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import ContentWrapper from "../../components/content-wrapper";
import { useLocaleProvider } from "../../components/locale-provider";
import { SectionHeading } from "../../components/section-heading";
import { PropertyAPIPromiseClient } from "../../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import { GetPropertyTickersRequest } from "../../gen/witbooking/backoffice/property/v1/property_api_pb";
import { useUserAuthInfo } from "../../lib/auth";
import { useGrpcClient, useUserGrpcCall } from "../../lib/grpc";
import { useApiClient } from "../../lib/http";
import {
  generateBillingReportCsv,
  generateBillingReportHeaders,
  generateBillingReportRows,
} from "./application/BillingReport.service";
import { BillingTable } from "./components/BillingTable";
import { BillingEntity } from "./domain/Billing.entity";
import { useBillingReportApi } from "./infrastructure/billingReport.api";
import { billingReportMessages } from "./messages";

enum BillingActionType {
  INIT_BILLING_FETCH,
  SUCCESS_BILLING_FETCH,
  ERROR_BILLING_FETCH,
  FILTER_BILLINGS,
}

interface BillingState {
  isLoading: boolean;
  totalBillings?: BillingEntity[];
  filteredBillings?: BillingEntity[];
  error?: Error;
}

interface BillingActionState {
  type: BillingActionType;
  billings?: BillingEntity[];
  search?: string;
  error?: Error;
}

function billingReducer(state: BillingState, action: BillingActionState): BillingState {
  switch (action.type) {
    case BillingActionType.INIT_BILLING_FETCH:
      return { isLoading: true };
    case BillingActionType.SUCCESS_BILLING_FETCH:
      return {
        isLoading: false,
        totalBillings: action.billings,
        filteredBillings: action.billings,
      };
    case BillingActionType.FILTER_BILLINGS:
      const { search } = action;
      if (!search) {
        return {
          ...state,
          filteredBillings: state.totalBillings,
        };
      } else {
        return {
          ...state,
          filteredBillings: state.totalBillings?.filter(it =>
            it.ticker.toLowerCase().includes(search.toLowerCase())
          ),
        };
      }
    case BillingActionType.ERROR_BILLING_FETCH:
      return {
        isLoading: false,
        totalBillings: undefined,
        filteredBillings: undefined,
        error: action.error,
      };
  }
}

export type OptionType = { value: string; label: string };

const INITIAL_STATE = { isLoading: false };
const THREE_YEARS_IN_MONTHS = 12 * 3;
const initialMonth = format(subMonths(new Date(), 1), "yyyy-MM-01");

function BillingReport({ type }: { type?: "septeo" }) {
  const { currentLocale } = useLocaleProvider();
  const dateOptions: OptionType[] = useMemo(
    () =>
      [...Array(THREE_YEARS_IN_MONTHS).keys()].map(idx => ({
        value: format(subMonths(new Date(), idx + 1), "yyyy-MM-01"),
        label: format(subMonths(new Date(), idx + 1), "MMMM yyyy", {
          locale: currentLocale === "es-ES" ? es : enGB,
        }),
      })),
    [currentLocale]
  );

  const [state, dispatch] = useReducer(billingReducer, INITIAL_STATE);
  const [period, setPeriod] = useState<OptionType>(dateOptions[0]);
  const [propertyTickers, setPropertyTickers] = useState<string[]>([]);

  const intl = useIntl();
  const user = useUserAuthInfo();
  const apiClient = useApiClient();
  const { getBillingReport } = useBillingReportApi(apiClient);

  const propertyGrpcClient = useGrpcClient(PropertyAPIPromiseClient);
  const getPropertyTickers = useUserGrpcCall(
    propertyGrpcClient,
    propertyGrpcClient.getAdminPropertyTickers
  );

  const isSepteo = useMemo(
    () => Boolean(type === "septeo" && (user?.isWitAdmin || user?.isCRMAdmin)),
    [type, user]
  );

  const headers = useMemo(
    () => generateBillingReportHeaders(currentLocale, user?.isWitAdmin ?? false, isSepteo),
    [currentLocale, user?.isWitAdmin, isSepteo]
  );

  const rows = useMemo(() => {
    if (!state.filteredBillings) return [];
    return generateBillingReportRows(
      state.filteredBillings,
      currentLocale,
      user?.isWitAdmin ?? false,
      isSepteo
    );
  }, [state.filteredBillings, currentLocale, user?.isWitAdmin, isSepteo]);

  const fetchReport = useCallback(
    async (month: string, tickers: string[]) => {
      dispatch({ type: BillingActionType.INIT_BILLING_FETCH });
      try {
        const data = await getBillingReport(month, tickers);
        dispatch({ type: BillingActionType.SUCCESS_BILLING_FETCH, billings: data });
      } catch (error) {
        dispatch({
          type: BillingActionType.ERROR_BILLING_FETCH,
          error: error instanceof Error ? error : undefined,
        });
      }
    },
    [getBillingReport]
  );

  useEffect(() => {
    async function fetchPropertyTickers() {
      const propertyTickersList = (
        await getPropertyTickers(new GetPropertyTickersRequest())
      ).getPropertyTickersList();
      setPropertyTickers(propertyTickersList);
    }
    fetchPropertyTickers().catch(err => console.error("Error loading user propertyTickers", err));
  }, [getPropertyTickers]);

  useEffect(() => {
    if (propertyTickers.length === 0) return;
    fetchReport(initialMonth, propertyTickers).then();
  }, [fetchReport, propertyTickers]);

  const handleFilter = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    dispatch({ type: BillingActionType.FILTER_BILLINGS, search });
  }, []);

  const handleMonthChange = useCallback(
    async (option: ValueType<OptionType, false>, _: ActionMeta<OptionType>) => {
      if (!option) return;

      setPeriod(option as OptionType);
      const month = (option as OptionType).value;
      await fetchReport(month, propertyTickers);
    },
    [fetchReport, propertyTickers]
  );

  const handleExport = useCallback(
    (onlyVariables?: boolean) => {
      if (!state.filteredBillings) return;
      const isWitAdmin = user?.isWitAdmin ?? false;
      generateBillingReportCsv({
        billings: state.filteredBillings,
        locale: currentLocale,
        isWitAdmin,
        period,
        isSepteo,
        onlyVariables,
      });
    },
    [state.filteredBillings, currentLocale, period, user?.isWitAdmin, isSepteo]
  );

  const handleExportVariablesClick = useCallback(() => {
    handleExport(true);
  }, [handleExport]);

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...billingReportMessages.title} />
      </SectionHeading>
      <div className="m-4 flex flex-col gap-4">
        <div className="flex gap-4">
          <Select
            className="!w-44"
            defaultValue={dateOptions[0]}
            options={dateOptions}
            onChange={handleMonthChange}
          />
          <Form.Control
            className="!w-60 !h-[38px]"
            placeholder={intl.formatMessage(billingReportMessages.inputFilterPlaceholder)}
            onChange={handleFilter}
          />
          <Button variant="primary" onClick={() => handleExport()}>
            <FormattedMessage {...billingReportMessages.btnExport} />
          </Button>
          {isSepteo && (
            <Button variant="primary" onClick={handleExportVariablesClick}>
              <FormattedMessage {...billingReportMessages.btnExportVariables} />
            </Button>
          )}
        </div>
        <Card>
          <Card.Body>
            {state.error ? (
              <Alert className="m-0" variant="danger">
                <FormattedMessage {...billingReportMessages.errorMessage} />
              </Alert>
            ) : (
              <BillingTable isLoading={state.isLoading} headers={headers} rows={rows} />
            )}
          </Card.Body>
        </Card>
      </div>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(BillingReport);
